// Edge calls for shared tastings (apps/edge/src/tastingShares.ts), one function per route like
// cardsApi.ts. A share is parsed before it leaves the device, so one the edge would refuse is a
// rejection here and never a request.
import {
  OkResponse,
  PutReactionRequest,
  PutTastingShareRequest,
  TastingShareList,
  TastingShareView,
  type ReactionKind,
} from "@proef/contract";
import { call, EdgeApiError } from "../edgeApi";
import { getAuthState } from "./auth";

const id = encodeURIComponent;

/** Shares one tasting; the same tasting again replaces what was shared (audience and line too). */
export async function putTastingShare(
  tastingId: string,
  request: PutTastingShareRequest,
): Promise<TastingShareView> {
  return await call(TastingShareView, `/v1/tastings/${id(tastingId)}/share`, {
    method: "PUT",
    body: JSON.stringify(PutTastingShareRequest.parse(request)),
  });
}

/** The caller's own share of a tasting, with who sees it; undefined when it is not shared. */
export async function getTastingShare(tastingId: string): Promise<TastingShareView | undefined> {
  try {
    return await call(TastingShareView, `/v1/tastings/${id(tastingId)}/share`);
  } catch (err) {
    if (err instanceof EdgeApiError && err.status === 404) return undefined;
    throw err;
  }
}

/** "Niet meer delen": removes the share for everyone it was shown to. Safe to repeat. */
export function deleteTastingShare(tastingId: string): Promise<OkResponse> {
  return call(OkResponse, `/v1/tastings/${id(tastingId)}/share`, { method: "DELETE" });
}

/**
 * A deleted tasting must not stay on in friends' feeds, so deleting one withdraws its share first;
 * a failure stops the deletion rather than leaving the share behind. Signed out there is no share.
 */
export async function withdrawShareOf(tastingId: string): Promise<void> {
  if (getAuthState().status !== "signedIn") return;
  await deleteTastingShare(tastingId);
}

/** What friends and groups shared, newest first; pass the previous page's `nextCursor` for more. */
export function listTastingShares(
  options: { limit?: number; cursor?: string } = {},
): Promise<TastingShareList> {
  const query = [
    ...(options.limit === undefined ? [] : [`limit=${String(options.limit)}`]),
    ...(options.cursor === undefined ? [] : [`cursor=${id(options.cursor)}`]),
  ].join("&");
  return call(TastingShareList, `/v1/tasting-shares${query === "" ? "" : `?${query}`}`);
}

export function putShareReaction(shareId: string, kind: ReactionKind): Promise<TastingShareView> {
  return call(TastingShareView, `/v1/tasting-shares/${id(shareId)}/reaction`, {
    method: "PUT",
    body: JSON.stringify(PutReactionRequest.parse({ kind })),
  });
}

export function deleteShareReaction(shareId: string): Promise<TastingShareView> {
  return call(TastingShareView, `/v1/tasting-shares/${id(shareId)}/reaction`, {
    method: "DELETE",
  });
}
